/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 全部照片页面（使用新的 Redux Slice）
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-05-20 16:22:00
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { Layout, Tabs, Badge, message, Button } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import type { DepartmentMemberV2 } from '../../global'
import parseQueryString from '../../utils/parseQueryString'
import PhotoFilter from './components/PhotoFilter'
import PhotosList from './PhotosList'
import PhotosListPrecise from './PhotosListPrecise'
import PhotosListByUserAgg from './PhotosListByUserAgg'
import { buildFetchPhotosParamsWithOptions } from './lib/photo'
import {
  fetchPhotosV2,
  fetchMorePhotosV2,
  fetchSuggestedPhotosV2,
  clearCheck,
  clearSuggestedCheck,
  setSuggestedWords,
  setLastPageCond,
  selectPhotosNum,
  selectExpandInfo,
  setIsAscSort as setContentIsAscSort,
  reset as resetPhotosByContent,
  setFallbackType,
  setFallbackKeyword,
  setSuggestedQueryFilter,
  setSuggestedPhotos
} from '../../slices/photosByContentSlice'
import { setLoading } from '../../actions/main'
import {
  setUiFilter as setPhotoFilterUi,
  setSearchFilter as setPhotoFilterSearch,
  selectSearchFilter,
  setSearchTrackingId
} from '../../slices/photoFilterSlice'
import { searchDepartmentMember } from '../../actions/group'
import { capturePromiseException } from '../../utils/sentryUtils'
import { abtest } from '../../utils/sensors'
import { setAIVisualSearchAB } from '../../actions/photo'
import {
  fetchNlpFilterResult,
  fetchVectorizedSuggestedSearch
} from '../components/PhotoFilter/services/nlpFilterService'
import type { DepartmentV2 } from './components/PhotoFilter/types'
import CONFIG from '../../constants/config'
import DownloadHistory from '../components/DownloadHistory'
import ClientDownloadDrawer from '../components/ClientDownloadDrawer'
import AiPhotoJunPanel from './components/AiPhotoJunPanel'
import AiQuickAskCard from './components/AiQuickAskCard'
import { IconSparkle, IconAiStar } from './components/aiPhotoJunIcons'
import GuideSparkle from './images/guide/guide-sparkle.svg?react'
import GuideExPhoto from './images/guide/guide-ex-photo.svg?react'
import GuideExList from './images/guide/guide-ex-list.svg?react'
import GuideExChart from './images/guide/guide-ex-chart.svg?react'
import GuideArrow from './images/guide/guide-arrow.svg?react'
import guideArtUrl from './images/guide/guide-art.jpg'
import guideGiftUrl from './images/guide/guide-gift.svg'
import {
  trackAiB8113Exposure,
  trackAiB8113Click
} from './utils/aiPhotoJunTrack'
import { setPhotoListActiveTab } from '../../slices/allPhotoSlice'
import {
  fetchPhotosByPrecise,
  reset as resetPhotosByPrecise,
  selectPhotosNum as selectPrecisePhotosNum,
  selectPreciseSearch,
  selectShowRedDot,
  selectIsAscSort as selectPreciseIsAscSort,
  setPreciseSearch,
  setShowRedDot,
  setIsAscSort as setPreciseIsAscSort
} from '../../slices/photosByPreciseSlice'
import type { FilterState } from './components/PhotoFilter/types'
import { getInitialFilter } from './components/PhotoFilter/utils/filterUtils'
import type { RootState } from '../../app/store'
import type {
  ExpandInfo,
  FetchPhotosResponse,
  PhotosSuggestedSearchWordItem
} from './types'
import { trackPicsPageClickV2 } from './utils/track'
import { registerMcpFromAdmin } from './utils/mcpRegister'
import './styles/index.scss'

function isPositiveSuggestedTotal(total: unknown): boolean {
  if (total === null || total === undefined || total === '') return false
  const n = typeof total === 'number' ? total : Number(total)
  return !Number.isNaN(n) && n > 0
}

/** 主 photos 返回的 suggestedSearchWords 中，仅保留 total>0 的词，供猜你想搜展示 */
function buildSuggestedWordsFromPhotosResponse(
  words: PhotosSuggestedSearchWordItem[] | undefined
): Array<{ word: string; count: number; isChecked: boolean }> {
  if (!words?.length) return []
  return words
    .filter(
      (w) =>
        w &&
        typeof w.word === 'string' &&
        w.word.trim() &&
        isPositiveSuggestedTotal(w.total)
    )
    .map((w) => ({
      word: w.word.trim(),
      count: typeof w.total === 'number' ? w.total : Number(w.total) || 0,
      isChecked: true
    }))
}

const { Content } = Layout
const { TabPane } = Tabs

declare const __API_SERVER_HOST__: string

/** GET /web/xiaohei-claw/preset_questions 响应: 后端按团队下发的个性化快捷问题 */
interface PresetQuestionsResponse {
  group_id?: string
  preset_questions?: string[]
}

/**
 * 全部照片页面
 */
const AllPhotoV2: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const lastInitKeyRef = useRef<string>('')
  const photoListRef = useRef<any>(null)
  const latestFilterRef = useRef<FilterState | null>(null)
  const lastExpandInfoRef = useRef<ExpandInfo | null>(null)
  const [formPlace, setFormPlace] = useState('allPhoto')
  const [currentSearchType, setCurrentSearchType] = useState<
    '普通猜你想搜' | '向量化猜你想搜'
  >('普通猜你想搜')
  const [aiPanelVisible, setAiPanelVisible] = useState(false)
  /**
   * 本次打开 AI 面板的入口来源(ai_b8112_exposure.fromPlace, 平台枚举仅两值)。
   * 各打开调用点"先写 ref 再切 visible"; 写后 setState 触发重渲染,
   * 面板拿到的 prop 即最新值。引导气泡=新功能弹窗, 其余入口=按钮。
   */
  const aiPanelFromPlaceRef = useRef<'AI问照片新功能弹窗' | '点击AI问照片按钮'>(
    '点击AI问照片按钮'
  )
  const [hoverCardVisible, setHoverCardVisible] = useState(false)
  const [pendingQuestion, setPendingQuestion] = useState('')
  // hover 卡片的快捷问题: 后端按团队下发, 首次 hover 懒加载一次 (ref 防重复请求)
  const [presetQuestions, setPresetQuestions] = useState<string[]>([])
  // true=后端快捷问题还在拉取中; 拉完(成功/失败)置 false。loading 时卡片不渲染问题, 避免闪兜底。
  const [presetLoading, setPresetLoading] = useState(true)
  const presetQuestionsLoadedRef = useRef(false)

  // 获取状态
  const groupId = useSelector((state: RootState) => state.group.groupId)
  const photosNum = useSelector(selectPhotosNum)
  const expandInfo = useSelector(selectExpandInfo)
  const currentQueryFilter = useSelector(selectSearchFilter)
  const precisePhotosNum = useSelector(selectPrecisePhotosNum)
  const preciseSearchState = useSelector(selectPreciseSearch)
  const showPreciseRedDot = useSelector(selectShowRedDot)
  const preciseIsAscSort = useSelector(selectPreciseIsAscSort)
  const activeTab = useSelector(
    (state: RootState) => state.allPhoto.photoListActiveTab
  )
  const photosByLocationNum = useSelector(
    (state: RootState) => state.photosByLocation?.photosNum || 0
  )
  const userId = useSelector((state: any) => state.main?.user?.user_id)
  const userName = useSelector((state: any) => state.main?.user?.nickname)
  // AI 面板默认展开黑名单查询结果: null=查询中, true=命中(user/group, 不展开), false=不命中(展开)。
  // 2026-06-22 去掉 AINativeAllPhotoPage AB, 改全量默认展开 + 后端黑名单兜底(详见下方 effect)。
  const [aiPanelBlocked, setAiPanelBlocked] = useState<boolean | null>(null)

  /**
   * AI 问照片(AI 照片王)入口:2026-06-12 去掉 AB 实验, 全量放开。
   * 所有团队全部成员统一展示入口(keywordsRowAfter button + 引导气泡 +
   * AiPhotoJunPanel 浮窗), 不再按 TheKingOfAIPhotos 实验分流。
   */
  const showAiPhotoKingEntry = true

  useEffect(() => {
    // AB 实验门:不展示入口的用户没必要预注册 MCP 凭据,
    // 避免对照组用户产生无效的 /mcp/register_from_mcp_admin 调用。
    if (!userId || !showAiPhotoKingEntry) return
    void registerMcpFromAdmin(String(userId))
  }, [userId, showAiPhotoKingEntry])

  /**
   * 浮窗化后 AI照片王不再占 flex 布局位,用 fixed 浮层 + FAB 切换。
   * 组件内部始终挂载,靠 display:none 隐藏,以保留对话状态 + 进行中的 SSE 流。
   */
  const toggleAiPanel = useCallback(() => {
    setAiPanelVisible((prev) => !prev)
  }, [])

  const closeAiPanel = useCallback(() => {
    setAiPanelVisible(false)
  }, [])

  const aiExposureFiredRef = useRef(false)
  useEffect(() => {
    if (aiExposureFiredRef.current) return
    if (!showAiPhotoKingEntry) return
    aiExposureFiredRef.current = true
    trackPicsPageClickV2({
      clickItem: 'AI问照片曝光',
      requirement_id: 1093
    })
  }, [showAiPhotoKingEntry])

  const AI_GUIDE_KEY = 'ai_photo_entry_guide_shown'
  const [showAiGuide, setShowAiGuide] = useState(false)
  const aiGuideRef = useRef<HTMLDivElement>(null)
  // 引导气泡只在「终态」才置 true, 避免挂载/切团队时闪现 (RFCT-34):
  // 默认行为是 AI 面板自动展开 (见下方 auto-open effect), 此时不该再弹引导气泡。
  // 只有命中黑名单 (aiPanelBlocked === true, 面板不会自动展开) 且未被 localStorage 节流时才展示。
  // 必须等黑名单查询有结果 (aiPanelBlocked !== null) 再判定, 否则会先 true (userId 就绪)
  // 再被 auto-open effect 改回 false (黑名单返回不命中) → 气泡闪一下。
  useEffect(() => {
    if (!userId) return
    if (aiPanelBlocked !== true) return // 未命中黑名单 → 面板自动展开, 不展示引导气泡
    const storageKey = `${AI_GUIDE_KEY}_${userId}`
    if (!localStorage.getItem(storageKey)) {
      setShowAiGuide(true)
    }
  }, [userId, aiPanelBlocked])
  // 神策埋点 ai_b8113_exposure: 引导气泡曝光时上报 (受用户 localStorage 节流, 只在首次出现时触发)
  useEffect(() => {
    if (showAiGuide) trackAiB8113Exposure()
  }, [showAiGuide])
  /**
   * AI 面板默认展开黑名单查询(2026-06-22 替代 AINativeAllPhotoPage AB):
   * 全量默认展开, 但后端黑名单命中的 user / group 不展开。fail-open——
   * 查询失败/超时按"不命中"处理(照常展开), 不让黑名单接口故障影响绝大多数用户。
   */
  useEffect(() => {
    if (!userId) return
    let aborted = false
    const url = `${__API_SERVER_HOST__}/web/xiaohei-claw/ai_panel/blacklist?user_id=${encodeURIComponent(
      String(userId)
    )}&group_id=${encodeURIComponent(String(groupId || ''))}`
    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`ai_panel/blacklist HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (!aborted) setAiPanelBlocked(!!data?.blocked)
      })
      .catch(() => {
        if (!aborted) setAiPanelBlocked(false) // fail-open: 查询失败默认展开
      })
    return () => {
      aborted = true
    }
  }, [userId, groupId])

  /**
   * 全量默认展开 AI 对话窗, 黑名单命中的不展开。
   * - 等黑名单查询有结果(aiPanelBlocked !== null)再决定, 避免命中用户"先展开再收起"的闪烁。
   * - aiPanelAutoOpenedRef 保证只自动展开一次:用户手动关掉后不再被重新弹开。
   * - 未命中黑名单(自动展开)时引导气泡本就不会置 true(见上方 showAiGuide effect 已按
   *   aiPanelBlocked===true 门控), 故此处无需再 setShowAiGuide(false), 二者不再冲突。
   */
  const aiPanelAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (aiPanelAutoOpenedRef.current) return
    if (!showAiPhotoKingEntry) return
    if (aiPanelBlocked === null) return // 黑名单查询未完成, 等结果
    if (aiPanelBlocked) return // 命中黑名单, 不自动展开
    aiPanelAutoOpenedRef.current = true
    setAiPanelVisible(true)
  }, [aiPanelBlocked, showAiPhotoKingEntry])
  /**
   * @param trackClose 是否上报 ai_b8113_click=关闭弹窗 神策埋点。
   *   立即体验 path 已在按钮 onClick 里独立上报"立即体验", 然后调本函数关气泡,
   *   此时传 false 避免双上报。其它入口(点外面 / 焦点丢失) 默认上报"关闭弹窗"。
   */
  const dismissAiGuide = useCallback(
    (trackClose = true) => {
      if (trackClose) trackAiB8113Click('关闭弹窗')
      setShowAiGuide(false)
      if (userId) {
        localStorage.setItem(`${AI_GUIDE_KEY}_${userId}`, '1')
      }
    },
    [userId]
  )
  useEffect(() => {
    if (!showAiGuide) return
    const handleClick = (e: MouseEvent) => {
      if (
        aiGuideRef.current &&
        !aiGuideRef.current.contains(e.target as Node)
      ) {
        dismissAiGuide()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showAiGuide, dismissAiGuide])

  /**
   * AI照片王 apply_page_filter 联动入口。
   *
   * 来源有两处:
   *   1. agent 调 apply_page_filter 工具 → SSE 事件
   *   2. 用户点 RankingCard 里 value(127 张) → 浮窗里合成的"看某人的照片"
   *
   * 动作:
   *   - 把 keywords / user_ids / start_time / end_time 转成 FilterState
   *   - 打上 isNlpFilter=true + nlQuery,让主页蓝 chip 能显示
   *   - dispatch setPhotoFilterSearch → 触发 useEffect 重拉 photosByContent
   *   - 滚动主照片列表到顶部,让用户能直接看到结果
   */
  const handleApplyPageFilter = useCallback(
    (payload: {
      nl_query: string
      keywords?: string[]
      user_ids?: string[]
      exclude_user_ids?: string[]
      department_ids?: string[]
      start_time?: string
      end_time?: string
      source_types?: number[]
      media_types?: number[]
      watermarks?: Array<{
        watermark_base_id?: string
        group_watermark_id?: string
      }>
      locations?: Array<{
        name: string
        detail?: string
        type?: number
        poi_type?: number
      }>
      location_key?: string
      exclude_keywords?: string[]
      exist_watermark?: number
      label_ids?: string[]
      label_match_all?: boolean
      total?: number
      /** user_ids 同序的展示名(RankingCard 跳转时携带,base 找不到 user 时 fallback 到这个) */
      user_display_names?: string[]
      /** AI 搜索 chip 关闭埋点用: 哪次 AI 会话产生 */
      source_session_id?: string
      /** AI 搜索 chip 关闭埋点用: 哪条 AI 回答消息(后端 message_id) */
      source_msg_id?: string
    }) => {
      if (!groupId) return
      const base = latestFilterRef.current
      // 按 user_id resolve 成员;匹配不到的合成 placeholder,后端按 id 过滤仍然生效
      // 1x1 透明 png 当 avatar 占位 —— 避免老组件 SelectedDepartments 拼 `${avatar}?x-oss-process=...`
      // 时把空串拼成相对路径,触发把当前页面当图片加载的诡异渲染
      const placeholderAvatar =
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII='
      const resolvedUsers: DepartmentMemberV2[] = (payload.user_ids || []).map(
        (uid, idx) => {
          const existing = base?.departmentUsers?.find(
            (u) => u.userID === uid || u.id === uid
          )
          if (existing) return existing
          // resolve 不到(离职 / 未加载)时合成占位
          // name 优先用 RankingCard 跳转时随 payload 带来的 user_display_names[idx](真人姓名),
          // 没有 → name 留空字符串,让上层 chip 显示"已选 N 人"兜底,**绝不暴露 xuser-xxx 这种 ID**
          // (旧逻辑 fallback 到 uid 直接把 ID 露给用户,刘乾隆 2026-04 反馈过)
          const fallbackName = payload.user_display_names?.[idx] || ''
          return {
            userID: uid,
            id: uid,
            name: fallbackName,
            avatar: placeholderAvatar,
            quit: false,
            role: 0
          } as DepartmentMemberV2
        }
      )
      // 部门 resolve:匹配不到时合成占位,后端按 id 过滤仍然生效
      const resolvedDepts: DepartmentV2[] = (payload.department_ids || []).map(
        (did) => {
          const existing = base?.departments?.find((d) => d.id === did)
          if (existing) return existing
          return { id: did, name: did, num: 0 } as DepartmentV2
        }
      )
      // 时间解析:start_time / end_time 为 "2006-01-02 15:04:05" 格式。
      // **REPLACE 语义(2026-04-29 张璇反馈修复)**:apply_page_filter 是"AI 完整 query 同步到主页",
      // payload 不含时间字段时,主页时间过滤必须**清空**,而不是沿用上一轮的 `base?.times`。
      // 旧 PATCH 行为下,用户问"全部照片"AI 推过来不带 time,主页却继续保留上一轮的 1.1-1.31,
      // 数字会从 19275 张缩水到 1119 张,跟 chip 文案"全部照片"对不上。
      let times: [dayjs.Dayjs, dayjs.Dayjs] | null = null
      if (payload.start_time && payload.end_time) {
        const s = dayjs(payload.start_time)
        const e = dayjs(payload.end_time)
        if (s.isValid() && e.isValid()) {
          times = [s, e]
        }
      }
      // V5 主页支持单值 mediaType / sourceType,而 payload 来自 photoMcp 是数组(可多选)。
      // 当前主页只取第一个,多选场景待主页 PhotoFilter 支持。
      const firstSourceType =
        payload.source_types && payload.source_types.length > 0
          ? payload.source_types[0]
          : null
      const firstMediaType =
        payload.media_types && payload.media_types.length > 0
          ? payload.media_types[0]
          : null
      // 水印数组在 FilterState 里是 string[](watermark_base_id 列表),做平铺映射
      const watermarkIds: string[] = (payload.watermarks || [])
        .map((w) => w.watermark_base_id || '')
        .filter(Boolean)
      // FilterState.locations 要求 detail/type 必填(非 undefined),给空字符串和 0 默认
      const normalizedLocations = payload.locations
        ? payload.locations.map((l) => ({
            name: l.name,
            detail: l.detail || '',
            type: l.type ?? 0
          }))
        : undefined
      const nextFilter: FilterState = {
        ...(base || ({} as FilterState)),
        groupByUser: false, // AI 问照片始终走平面列表，关闭按人员聚合
        // 主页搜索框输入文本(keyword)清掉:AI 搜索 chip 已经显示 nlQuery,
        // 旧的输入框残留(比如上一轮的"3月30日-4月29日拍的")会跟 chip 重复展示,
        // 视觉上很乱。每次 AI 同步过来都重置成空(2026-04-29 张璇反馈)。
        keyword: '',
        searchKeywords: payload.keywords || [],
        // 排除关键词:之前漏接,导致"含 X 但排除 Y"同步到主页后 Y 没被排除
        //(茸梅路事故:含茸梅但排除茸梅路,主页仍返回 593 含茸梅路,而非 429)
        excludeKeywords: payload.exclude_keywords || [],
        suggestedSearchWords: [],
        departmentUsers: resolvedUsers,
        excludeUserIDs: payload.exclude_user_ids || [],
        departments: resolvedDepts,
        times,
        sourceType: firstSourceType,
        mediaType: firstMediaType,
        watermarks: watermarkIds,
        locations: normalizedLocations,
        existWatermark: payload.exist_watermark,
        labelIDs: payload.label_ids,
        labelMatchAll: payload.label_match_all,
        // location_key 走 v5 LocationRecommend 通道(地点关键词模糊搜索),与 locations 结构化筛选互补:
        // - locations:[{name,type,poi_type}] 是结构化(知道完整 SearchLocation 时用)
        // - locationRecommend:{word/key} 是模糊关键词(只知道地名字符串如"北京"时用)
        // AI 照片君按城市/区/POI 排行时,filter_payload.location_key 由 photoMcp echo 过来,这里转成 v5 字段
        locationRecommend: payload.location_key
          ? { key: payload.location_key, word: payload.location_key }
          : null,
        isNlpFilter: true,
        nlQuery: payload.nl_query,
        // 神策埋点 web_workgroup_picspage_click(clickItem=点击关闭AI搜索chip)用,
        // 用户后来点 chip × 关闭时, clearAiSearchChip 从这里读出 sessionID/msgID 上报。
        aiSourceSessionId: payload.source_session_id,
        aiSourceMsgId: payload.source_msg_id
      }
      // 先把 slice 状态推一轮 —— AI 搜索 chip 从 searchFilter 读 isNlpFilter + nlQuery,
      // 必须在 handleFilterSubmit 拉照片之前就落到 Redux,否则 chip 不显示。
      dispatch(setPhotoFilterUi(nextFilter))
      dispatch(setPhotoFilterSearch(nextFilter))
      // 用已有的 handleFilterSubmit 路径,省一堆 boilerplate;它会处理清选中、重置排序、拉照片、埋点等
      void handleFilterSubmit(nextFilter)
      // 自动打开浮窗(如果用户此时从 FAB 态点了排行榜按钮),但不用强制
      aiPanelFromPlaceRef.current = '点击AI问照片按钮'
      setAiPanelVisible(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupId]
  )

  /**
   * Ai照片君 浮窗里 StatCard 大数字点击 → 把 nl_query 灌回主页搜索框走自己的 NLP 链路。
   *
   * 与 handleApplyPageFilter 不同:那个是"agent 已经把过滤条件结构化好了,直接 dispatch",
   * 本函数是"我们只有一句自然语言,要走主页 by_nlp 接口让它自己解析"——两边走同一个 NLP 服务,
   * 数字理论上和 stat 卡片一致。
   *
   * 主要镜像 useNlpKeywordSubmit.handleSearch 里 isNlpFilter=true 的成功分支,但跳过 polishing
   * (这是探索性映射,先把功能跑通)。
   */
  const handleApplyNlQuery = useCallback(
    async (text: string) => {
      if (!groupId) return
      const trimmed = text.trim()
      if (!trimmed) return
      const base = latestFilterRef.current
      const buildFilter = (override: Partial<FilterState>): FilterState => ({
        ...(base || ({} as FilterState)),
        // 默认全清 NLP 之前的人/部门/keyword 选择,再用 override 覆盖
        searchKeywords: [],
        suggestedSearchWords: [],
        departmentUsers: [],
        excludeUserIDs: [],
        departments: [],
        ...override,
        // 主页搜索框输入文本(keyword)清掉:AI 搜索 chip 已经显示 nlQuery,
        // 之前把 trimmed(可能是"用户1227拍的"这种 NLP 句子)灌进 keyword,
        // 会在搜索框里看到「用户12272021年5月21日-2026年5月21日拍的」这种乱拼接,
        // 跟 chip 重复展示且文案不符合预期(2026-05-21 张璇反馈)。
        // 跟 handleApplyPageFilter 的做法对齐 —— 主页搜索框留空,nlQuery 走 chip。
        keyword: '',
        isNlpFilter: true,
        nlQuery: trimmed
      })

      let nextFilter: FilterState
      try {
        const res = await fetchNlpFilterResult({
          groupID: groupId,
          keyword: trimmed
        })
        const data = res?.data
        const ok =
          res?.code === 200 && data?.status === 0 && data?.isNlpFilter === true
        if (ok && data) {
          const f = data.filter || {}
          const departmentUsers: DepartmentMemberV2[] =
            f.userInfoList?.map((it) => ({
              id: it.id,
              userID: it.userID || it.id,
              name: it.name,
              avatar: it.avatar || '',
              quit: false,
              role: 0
            })) || []
          const departments: DepartmentV2[] =
            f.departmentInfoList?.map((it) => ({
              id: String(it.id),
              name: it.name,
              num: it.peopleNum,
              avatar: it.avatar
            })) || []
          let times: FilterState['times'] | undefined
          if (f.timeParams?.startTime && f.timeParams?.endTime) {
            const s = dayjs(f.timeParams.startTime)
            const e = dayjs(f.timeParams.endTime)
            if (s.isValid() && e.isValid()) times = [s, e]
          }
          const searchKeywords =
            f.keywords && f.keywords.length > 0 ? f.keywords : []
          nextFilter = buildFilter({
            departmentUsers,
            departments,
            ...(times ? { times } : {}),
            searchKeywords,
            nlpResponseJson: JSON.stringify(res),
            ...(f.departmentInfoList && f.departmentInfoList.length > 0
              ? { nlpDepartmentList: f.departmentInfoList.map((it) => it.name) }
              : {})
          })
        } else {
          // NLP 解析没命中或返回 isNlpFilter=false → 退化成纯关键词搜
          nextFilter = buildFilter({
            searchKeywords: [trimmed],
            isNlpFilter: false
          })
        }
      } catch (err) {
        console.error('handleApplyNlQuery: fetchNlpFilterResult 失败', err)
        nextFilter = buildFilter({
          searchKeywords: [trimmed],
          isNlpFilter: false
        })
      }

      dispatch(setPhotoFilterUi(nextFilter))
      dispatch(setPhotoFilterSearch(nextFilter))
      void handleFilterSubmit(nextFilter)
      aiPanelFromPlaceRef.current = '点击AI问照片按钮'
      setAiPanelVisible(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupId]
  )

  /** 清掉 AI 搜索 chip:重置成默认 filter(近 6 个月、空关键词) */
  const clearAiSearchChip = useCallback(() => {
    const base = latestFilterRef.current
    if (!base) return
    // 旧埋点改造 (rid=1093 web_workgroup_picspage_click): 新增 clickItem=点击关闭AI搜索chip
    // sessionID/msgID 是 AiPhotoJunPanel 推 apply_page_filter 时塞进 FilterState 的,
    // 这里读出来一起上报(spec 标注"该 clickItem 时上报")。
    trackPicsPageClickV2({
      clickItem: '点击关闭AI搜索chip',
      requirement_id: 1093,
      sessionID: base.aiSourceSessionId || '',
      msgID: base.aiSourceMsgId || ''
    })
    const cleared: FilterState = {
      ...base,
      searchKeywords: [],
      suggestedSearchWords: [],
      departmentUsers: [],
      excludeUserIDs: [],
      times: [
        dayjs().subtract(6, 'months').startOf('day'),
        dayjs().endOf('day')
      ],
      isNlpFilter: false,
      nlQuery: undefined,
      aiSourceSessionId: undefined,
      aiSourceMsgId: undefined
    }
    dispatch(setPhotoFilterUi(cleared))
    dispatch(setPhotoFilterSearch(cleared))
    void handleFilterSubmit(cleared)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * 封装猜你想搜及无结果推荐策略的获取逻辑
   */
  const fetchSuggestedData = useCallback(
    async (
      filter: FilterState,
      currentGroupId: string,
      hasResults: boolean,
      mainPhotosFetchResult?: FetchPhotosResponse | null
    ) => {
      // 1. 如果原始查询有结果，仅执行 P1 猜你想搜逻辑（追加在底部）
      if (hasResults) {
        dispatch(setFallbackType('none'))

        const clearSuggestedState = () => {
          dispatch(setSuggestedWords([]))
          dispatch(setSuggestedPhotos([]))
          dispatch(clearSuggestedCheck())
          dispatch(setSuggestedQueryFilter(null))
        }

        /** count 接口无有效词时，使用主 photos 返回的 suggestedSearchWords（total>0） */
        const trySuggestedWordsFromMainPhotos = async (): Promise<boolean> => {
          const fromPhotosList = buildSuggestedWordsFromPhotosResponse(
            mainPhotosFetchResult?.suggestedSearchWords
          )
          if (fromPhotosList.length === 0) return false
          const p1Filter = { ...filter }
          const suggestedPhotoParams = buildFetchPhotosParamsWithOptions({
            queryFilter: {
              ...p1Filter,
              searchKeywords: [],
              suggestedSearchWords: fromPhotosList.filter((w) => w.isChecked)
            },
            groupID: currentGroupId,
            isAscSort: false
          })
          try {
            const p1bResult = await (
              dispatch(fetchSuggestedPhotosV2(suggestedPhotoParams)) as any
            ).unwrap()
            if (isPositiveSuggestedTotal(p1bResult?.total)) {
              batch(() => {
                dispatch(setFallbackType('P1'))
                dispatch(
                  setFallbackKeyword(
                    filter.searchKeywords?.length === 1
                      ? filter.searchKeywords[0]
                      : filter.searchKeywords?.join(' ') || ''
                  )
                )
                dispatch(setSuggestedWords(fromPhotosList))
                dispatch(clearSuggestedCheck())
                dispatch(setSuggestedQueryFilter(p1Filter))
              })
              return true
            }
            clearSuggestedState()
            return false
          } catch (err) {
            console.error('猜你想搜(主 photos 推荐词)拉取失败:', err)
            clearSuggestedState()
            return false
          }
        }

        if (filter.searchKeywords && filter.searchKeywords.length === 1) {
          const keyword = filter.searchKeywords[0]
          try {
            const suggestedParams = {
              groupID: currentGroupId,
              keyword,
              departmentIDs: filter.departments?.map((dept) => dept.id) || [],
              userIDs:
                filter.departmentUsers?.map((user) => user.userID || user.id) ||
                [],
              times: filter.times
                ? [
                    {
                      start: filter.times[0].format(CONFIG.timeFormat),
                      end: filter.times[1].format(CONFIG.timeFormat)
                    }
                  ]
                : undefined
            }

            const suggestedResult =
              await fetchVectorizedSuggestedSearch(suggestedParams)

            let suggestedWords: Array<{
              word: string
              count: number
              isChecked: boolean
            }> = []
            if (
              suggestedResult.code === 200 &&
              suggestedResult.data?.suggested &&
              suggestedResult.data.suggested.length > 0
            ) {
              suggestedWords = suggestedResult.data.suggested
                .filter((item: any) => item.exist)
                .map((item: any) => ({
                  word: item.word,
                  count: item.count,
                  isChecked: true
                }))
            }

            if (suggestedWords.length > 0) {
              const p1Filter = { ...filter }
              const suggestedPhotoParams = buildFetchPhotosParamsWithOptions({
                queryFilter: {
                  ...p1Filter,
                  searchKeywords: [],
                  suggestedSearchWords: suggestedWords.filter(
                    (w: any) => w.isChecked
                  )
                },
                groupID: currentGroupId,
                isAscSort: false
              })
              const p1WithMainList = await (
                dispatch(fetchSuggestedPhotosV2(suggestedPhotoParams)) as any
              ).unwrap()
              if (isPositiveSuggestedTotal(p1WithMainList?.total)) {
                batch(() => {
                  dispatch(setFallbackType('P1'))
                  dispatch(setFallbackKeyword(keyword))
                  dispatch(setSuggestedWords(suggestedWords))
                  dispatch(clearSuggestedCheck())
                  dispatch(setSuggestedQueryFilter(p1Filter))
                })
              } else {
                clearSuggestedState()
              }
            } else if (!(await trySuggestedWordsFromMainPhotos())) {
              clearSuggestedState()
            }
          } catch (error) {
            console.error('获取猜你想搜失败:', error)
            if (!(await trySuggestedWordsFromMainPhotos())) {
              clearSuggestedState()
            }
          }
        } else if (!(await trySuggestedWordsFromMainPhotos())) {
          clearSuggestedState()
        }
        return
      }

      // 2. 如果原始查询无结果，且输入框里有搜索内容，执行 P1 -> P2 -> P3 推荐逻辑
      let fallbackSucceeded = false
      const hasKeyword =
        filter.searchKeywords && filter.searchKeywords.length > 0

      // 如果没有任何查询词，那么不触发无结果推荐策略
      if (!hasKeyword) {
        dispatch(setFallbackType('none'))
        dispatch(setSuggestedWords([]))
        dispatch(setSuggestedPhotos([]))
        dispatch(clearSuggestedCheck())
        dispatch(setSuggestedQueryFilter(null))
        return
      }

      // P1: 猜你想搜关键词结果
      if (filter.searchKeywords && filter.searchKeywords.length === 1) {
        const keyword = filter.searchKeywords[0]
        try {
          const suggestedParams = {
            groupID: currentGroupId,
            keyword,
            departmentIDs: filter.departments?.map((dept) => dept.id) || [],
            userIDs:
              filter.departmentUsers?.map((user) => user.userID || user.id) ||
              [],
            times: filter.times
              ? [
                  {
                    start: filter.times[0].format(CONFIG.timeFormat),
                    end: filter.times[1].format(CONFIG.timeFormat)
                  }
                ]
              : undefined
          }

          const suggestedResult =
            await fetchVectorizedSuggestedSearch(suggestedParams)

          if (
            suggestedResult.code === 200 &&
            suggestedResult.data?.suggested &&
            suggestedResult.data.suggested.length > 0
          ) {
            const suggestedWords = suggestedResult.data.suggested
              .filter((item: any) => item.exist)
              .map((item: any) => ({
                word: item.word,
                count: item.count,
                isChecked: true
              }))

            if (suggestedWords.length > 0) {
              const suggestedPhotoParams = buildFetchPhotosParamsWithOptions({
                queryFilter: {
                  ...filter,
                  searchKeywords: [],
                  suggestedSearchWords: suggestedWords.filter(
                    (w: any) => w.isChecked
                  )
                },
                groupID: currentGroupId,
                isAscSort: false
              })

              const p1Result = await (
                dispatch(fetchSuggestedPhotosV2(suggestedPhotoParams)) as any
              ).unwrap()
              if (isPositiveSuggestedTotal(p1Result?.total)) {
                batch(() => {
                  dispatch(setFallbackType('P1'))
                  dispatch(setFallbackKeyword(keyword))
                  dispatch(setSuggestedWords(suggestedWords))
                  dispatch(clearSuggestedCheck())
                  dispatch(setSuggestedQueryFilter({ ...filter }))
                })
                fallbackSucceeded = true
              } else {
                dispatch(setSuggestedWords([]))
                dispatch(setSuggestedPhotos([]))
                dispatch(clearSuggestedCheck())
              }
            }
          }
        } catch (error) {
          console.error('获取猜你想搜 P1 失败:', error)
        }
      }

      // P1b：向量化 count 无有效词或 P1 未成功后，使用主 photos 返回的 suggestedSearchWords（需 total>0）
      if (!fallbackSucceeded) {
        const fromPhotosList = buildSuggestedWordsFromPhotosResponse(
          mainPhotosFetchResult?.suggestedSearchWords
        )
        if (fromPhotosList.length > 0) {
          try {
            const p1bPhotoParams = buildFetchPhotosParamsWithOptions({
              queryFilter: {
                ...filter,
                searchKeywords: [],
                suggestedSearchWords: fromPhotosList.filter((w) => w.isChecked)
              },
              groupID: currentGroupId,
              isAscSort: false
            })

            const p1bResult = await dispatch(
              fetchSuggestedPhotosV2(p1bPhotoParams) as any
            ).unwrap()

            if (isPositiveSuggestedTotal(p1bResult?.total)) {
              batch(() => {
                dispatch(setFallbackType('P1'))
                dispatch(
                  setFallbackKeyword(
                    filter.searchKeywords?.length === 1
                      ? filter.searchKeywords[0]
                      : filter.searchKeywords?.join(' ') || ''
                  )
                )
                dispatch(setSuggestedWords(fromPhotosList))
                dispatch(clearSuggestedCheck())
                dispatch(setSuggestedQueryFilter({ ...filter }))
              })
              fallbackSucceeded = true
            } else {
              dispatch(setSuggestedWords([]))
              dispatch(setSuggestedPhotos([]))
              dispatch(clearSuggestedCheck())
            }
          } catch (error) {
            console.error('获取猜你想搜 P1b(photos 推荐词) 失败:', error)
            dispatch(setSuggestedWords([]))
            dispatch(setSuggestedPhotos([]))
            dispatch(clearSuggestedCheck())
          }
        }
      }

      // P2: 放开时间范围的结果
      if (!fallbackSucceeded && filter.times && filter.times.length > 0) {
        try {
          // 清除时间条件
          const p2Filter = { ...filter, times: null }
          const p2Params = buildFetchPhotosParamsWithOptions({
            queryFilter: p2Filter,
            groupID: currentGroupId,
            isAscSort: false
          })

          // 强制清空时间数组为1970年至今
          p2Params.filter.times = [
            {
              start: dayjs('1970-01-01').format(CONFIG.timeFormat),
              end: dayjs().format(CONFIG.timeFormat)
            }
          ]

          // 请求猜你想搜词（放开时间）
          let suggestedWords: any[] = []
          if (filter.searchKeywords && filter.searchKeywords.length === 1) {
            const keyword = filter.searchKeywords[0]
            const suggestedParams = {
              groupID: currentGroupId,
              keyword,
              departmentIDs: filter.departments?.map((dept) => dept.id) || [],
              userIDs:
                filter.departmentUsers?.map((user) => user.userID || user.id) ||
                [],
              times: [
                {
                  start: dayjs('1970-01-01').format(CONFIG.timeFormat),
                  end: dayjs().format(CONFIG.timeFormat)
                }
              ]
            }
            const suggestedResult =
              await fetchVectorizedSuggestedSearch(suggestedParams)
            if (
              suggestedResult.code === 200 &&
              suggestedResult.data?.suggested &&
              suggestedResult.data.suggested.length > 0
            ) {
              suggestedWords = suggestedResult.data.suggested
                .filter((item: any) => item.exist)
                .map((item: any) => ({
                  word: item.word,
                  count: item.count,
                  isChecked: true
                }))
            }
          }

          if (suggestedWords.length > 0) {
            p2Params.filter.searchKeywords = []
            p2Params.filter.suggestedSearchWords = suggestedWords.filter(
              (w: any) => w.isChecked
            )
            p2Params.filter.useSuggestedSearch = true
          } else {
            p2Params.filter.suggestedSearchWords = []
            p2Params.filter.useSuggestedSearch = false
          }

          const p2Result = await (
            dispatch(fetchSuggestedPhotosV2(p2Params)) as any
          ).unwrap()

          if (isPositiveSuggestedTotal(p2Result?.total)) {
            const p2FilterFinal: FilterState = {
              ...filter,
              times: [dayjs('1970-01-01'), dayjs()] as [
                dayjs.Dayjs,
                dayjs.Dayjs
              ]
            }
            batch(() => {
              dispatch(setFallbackType('P2'))
              dispatch(
                setFallbackKeyword(filter.searchKeywords?.join(' ') || '')
              )
              dispatch(setSuggestedQueryFilter(p2FilterFinal))
              if (suggestedWords.length > 0) {
                dispatch(setSuggestedWords(suggestedWords))
              } else {
                dispatch(setSuggestedWords([]))
              }
              dispatch(clearSuggestedCheck())
            })
            fallbackSucceeded = true
          }
        } catch (error) {
          console.error('P2 推荐策略失败:', error)
        }
      }

      // P3: 放开人员筛选的结果
      if (
        !fallbackSucceeded &&
        ((filter.departments && filter.departments.length > 0) ||
          (filter.departmentUsers && filter.departmentUsers.length > 0))
      ) {
        try {
          const p3Filter = {
            ...filter,
            times: null,
            departments: [],
            departmentUsers: [],
            excludeUserIDs: []
          }
          const p3Params = buildFetchPhotosParamsWithOptions({
            queryFilter: p3Filter,
            groupID: currentGroupId,
            isAscSort: false
          })

          p3Params.filter.times = [
            {
              start: dayjs('1970-01-01').format(CONFIG.timeFormat),
              end: dayjs().format(CONFIG.timeFormat)
            }
          ]
          p3Params.filter.departmentIDList = []
          p3Params.filter.userIDList = []
          p3Params.filter.excludeUserIDList = []

          // 请求猜你想搜词（放开时间与人员）
          let suggestedWords: any[] = []
          if (filter.searchKeywords && filter.searchKeywords.length === 1) {
            const keyword = filter.searchKeywords[0]
            const suggestedParams = {
              groupID: currentGroupId,
              keyword,
              departmentIDs: [], // 人员不限
              userIDs: [], // 人员不限
              times: [
                {
                  start: dayjs('1970-01-01').format(CONFIG.timeFormat),
                  end: dayjs().format(CONFIG.timeFormat)
                }
              ]
            }
            const suggestedResult =
              await fetchVectorizedSuggestedSearch(suggestedParams)
            if (
              suggestedResult.code === 200 &&
              suggestedResult.data?.suggested &&
              suggestedResult.data.suggested.length > 0
            ) {
              suggestedWords = suggestedResult.data.suggested
                .filter((item: any) => item.exist)
                .map((item: any) => ({
                  word: item.word,
                  count: item.count,
                  isChecked: true
                }))
            }
          }

          if (suggestedWords.length > 0) {
            p3Params.filter.searchKeywords = []
            p3Params.filter.suggestedSearchWords = suggestedWords.filter(
              (w: any) => w.isChecked
            )
            p3Params.filter.useSuggestedSearch = true
          } else {
            p3Params.filter.suggestedSearchWords = []
            p3Params.filter.useSuggestedSearch = false
          }

          const p3Result = await (
            dispatch(fetchSuggestedPhotosV2(p3Params)) as any
          ).unwrap()

          if (isPositiveSuggestedTotal(p3Result?.total)) {
            const p3FilterFinal: FilterState = {
              ...filter,
              times: [dayjs('1970-01-01'), dayjs()] as [
                dayjs.Dayjs,
                dayjs.Dayjs
              ],
              departments: [],
              departmentUsers: [],
              excludeUserIDs: []
            }
            batch(() => {
              dispatch(setFallbackType('P3'))
              dispatch(
                setFallbackKeyword(filter.searchKeywords?.join(' ') || '')
              )
              dispatch(setSuggestedQueryFilter(p3FilterFinal))
              if (suggestedWords.length > 0) {
                dispatch(setSuggestedWords(suggestedWords))
              } else {
                dispatch(setSuggestedWords([]))
              }
              dispatch(clearSuggestedCheck())
            })
            fallbackSucceeded = true
          }
        } catch (error) {
          console.error('P3 推荐策略失败:', error)
        }
      }

      // 都没有命中
      if (!fallbackSucceeded) {
        dispatch(setFallbackType('none'))
        dispatch(setSuggestedWords([]))
        dispatch(setSuggestedPhotos([]))
        dispatch(clearSuggestedCheck())
        dispatch(setSuggestedQueryFilter(null))
      }
    },
    [dispatch]
  )

  const createDefaultFilter = (): FilterState => ({
    ...getInitialFilter(),
    existWatermark: undefined,
    existPhotoComment: undefined
  })

  const scrollPhotoListToTop = () => {
    if (photoListRef.current?.scrollToPosition) {
      photoListRef.current.scrollToPosition({
        scrollLeft: 0,
        scrollTop: 0
      })
    }
  }

  const fetchPreciseData = useCallback(
    async (filter: FilterState | null | undefined) => {
      if (!filter || !groupId) return

      const params = buildFetchPhotosParamsWithOptions({
        queryFilter: filter,
        groupID: groupId,
        isPreciseSearch: true,
        isAscSort: preciseIsAscSort,
        lastPageCond: ''
      })

      await dispatch(fetchPhotosByPrecise(params) as any)
    },
    [dispatch, groupId]
  )

  const handleExpandPrecise = useCallback(
    async (
      filter: FilterState | null | undefined,
      expandInfo?: ExpandInfo | null
    ) => {
      if (!filter) return

      const expandPrecise = expandInfo?.expandPrecise

      if (expandPrecise?.canPreciseSearch) {
        dispatch(setPreciseSearch(expandPrecise))
        dispatch(setShowRedDot(true))
        try {
          await fetchPreciseData(filter)
        } catch (error) {
          capturePromiseException(error as Error)
        }
      } else {
        dispatch(
          setPreciseSearch({
            canPreciseSearch: false,
            notWords: []
          })
        )
        dispatch(setShowRedDot(false))
        dispatch(resetPhotosByPrecise())
        dispatch(setPhotoListActiveTab('content'))
      }
    },
    [dispatch, fetchPreciseData]
  )

  // 初始化筛选条件
  const handleInitFilter = async (): Promise<FilterState | null> => {
    const {
      start_time,
      end_time,
      member_id,
      has_watermark,
      has_member,
      filter_item_content
    } = parseQueryString()

    // 获取初始筛选条件（从 photoFilterSlice 默认值）
    const initialFilter = createDefaultFilter()
    const queryFilter: FilterState = { ...initialFilter }

    // 处理 member_id
    if (member_id) {
      try {
        const result = await dispatch(
          searchDepartmentMember({ searchUserID: member_id })
        )
        const departmentMember = (result as any)?.member?.[0] || null
        if (departmentMember) {
          queryFilter.departmentUsers = [departmentMember]
          const nickname = departmentMember.nickname || ''
          if (nickname) {
            message.info(`已筛选"${nickname}"的全部照片`)
          }
        }
      } catch (error) {
        console.error('搜索部门成员失败:', error)
      }
    }

    // 处理时间范围
    if (start_time && end_time) {
      queryFilter.times = [
        dayjs(decodeURIComponent(start_time)),
        dayjs(decodeURIComponent(end_time))
      ]
    }

    // 处理关键词
    if (filter_item_content) {
      queryFilter.searchKeywords = [decodeURIComponent(filter_item_content)]
    }

    // 处理水印
    if (has_watermark) {
      const watermarks = sessionStorage.getItem('watermarks')
      if (watermarks) {
        try {
          queryFilter.watermarks = JSON.parse(watermarks)
        } catch (e) {
          console.error('解析 watermarks 失败:', e)
        }
      }
    }

    // 处理成员
    if (has_member) {
      const departments = sessionStorage.getItem('departments')
      if (departments) {
        try {
          queryFilter.departments = JSON.parse(departments)
        } catch (e) {
          console.error('解析 departments 失败:', e)
        }
      }

      const departmentUsers = sessionStorage.getItem('departmentUsers')
      if (departmentUsers) {
        try {
          queryFilter.departmentUsers = JSON.parse(departmentUsers)
        } catch (e) {
          console.error('解析 departmentUsers 失败:', e)
        }
      }
    }

    // 同步到 photoFilterSlice
    dispatch(setPhotoFilterUi(queryFilter))
    dispatch(setPhotoFilterSearch(queryFilter))
    latestFilterRef.current = queryFilter

    return queryFilter
  }

  // 处理筛选提交
  const handleFilterSubmit = async (filter: FilterState) => {
    if (!groupId) {
      message.error('请先选择团队')
      return
    }

    // 在 /photo/all 从按人员跳转进入时，用户一旦主动“搜索”，就移除返回标识（隐藏返回按钮）
    if (window.location.pathname === '/photo/all') {
      clearAggReturnParamsFromUrl()
    }

    // 清空选中状态
    dispatch(clearCheck())
    dispatch(setLastPageCond(''))
    // 重置所有标签页的排序状态为倒序
    dispatch(setContentIsAscSort(false))
    dispatch(setPreciseIsAscSort(false))
    // 重置photoList状态
    dispatch(resetPhotosByPrecise())
    dispatch(resetPhotosByContent())
    latestFilterRef.current = filter

    // 按人员聚合模式：只触发 agg/user，跳过 NLP / photos 接口
    if (filter.groupByUser) {
      // 交由 PhotosListByUserAgg 请求与渲染，这里只返回占位结果给埋点链路
      scrollPhotoListToTop()
      return {
        total: 0,
        expandInfo: null,
        suggestedSearchWords: [],
        photosByLocation: {
          photosNum: 0
        }
      }
    }

    dispatch(setLoading(true))

    // 构建请求参数
    const params = buildFetchPhotosParamsWithOptions({
      queryFilter: filter,
      groupID: groupId,
      isAscSort: false,
      // 把 FilterState.locationRecommend 透传到请求体的 LocationRecommend 字段。
      // 来源: AI 照片君 apply_page_filter 携带 location_key / 主页地点关键词搜索。
      locationRecommend: filter.locationRecommend ?? null
    })

    try {
      const result = await (dispatch(fetchPhotosV2(params)) as any).unwrap()

      // 滚动到顶部
      scrollPhotoListToTop()

      // 处理猜你想搜及无结果推荐逻辑
      const hasResults = result?.total > 0
      await fetchSuggestedData(filter, groupId, hasResults, result)

      // 返回结果数据，用于埋点
      return {
        total: result?.total || 0,
        expandInfo: result?.expandInfo || null,
        suggestedSearchWords: [],
        photosByLocation: {
          photosNum: photosByLocationNum
        }
      }
    } catch (error) {
      console.error('获取照片失败:', error)
      capturePromiseException(error as Error)
      return undefined
    } finally {
      dispatch(setLoading(false))
    }
  }

  // 处理筛选重置
  const handleFilterReset = async () => {
    if (!groupId) {
      message.error('请先选择团队')
      return
    }

    if (window.location.pathname === '/photo/all') {
      clearAggReturnParamsFromUrl()
    }

    dispatch(setLastPageCond(''))
    // 重置所有标签页的排序状态为倒序
    dispatch(setContentIsAscSort(false))
    dispatch(setPreciseIsAscSort(false))
    // 重置photoList状态
    dispatch(resetPhotosByPrecise())
    dispatch(resetPhotosByContent())
    dispatch(setLoading(true))

    const defaultFilter = createDefaultFilter()
    // 重置时强制把「按人员」置回 false（确保 UI 回到平面模式）
    dispatch(setPhotoFilterUi(defaultFilter))
    dispatch(setPhotoFilterSearch(defaultFilter))
    latestFilterRef.current = defaultFilter

    // 构建请求参数并获取照片
    const params = buildFetchPhotosParamsWithOptions({
      queryFilter: defaultFilter,
      groupID: groupId,
      isAscSort: false
    })

    try {
      const result = await (dispatch(fetchPhotosV2(params)) as any).unwrap()
      // 滚动到顶部
      scrollPhotoListToTop()

      // 重置筛选时也清空降级推荐相关状态
      dispatch(setFallbackType('none'))
      dispatch(setSuggestedWords([]))
      dispatch(setSuggestedPhotos([]))
      dispatch(clearSuggestedCheck())
      dispatch(setSuggestedQueryFilter(null))
    } catch (error) {
      console.error('重置筛选失败:', error)
      capturePromiseException(error as Error)
    } finally {
      dispatch(setLoading(false))
    }
  }

  // 组件挂载时初始化
  useEffect(() => {
    const init = async () => {
      // 同一路由组件下，query 变化也需要重新按 URL 恢复筛选（例如 /photo/all?member_id=xxx）
      // 用 ref 去重，避免重复初始化
      const nextKey = `${location.pathname}?${location.search || ''}`
      if (lastInitKeyRef.current === nextKey) return

      // 非首次进入时，只有当 URL 确实携带“筛选相关参数”才执行 URL -> filter 的覆盖初始化。
      // 否则（例如从按人员返回后 replace 到 /photo/all），保持 Redux 中已恢复的筛选不被覆盖。
      const isFirstInit = !lastInitKeyRef.current
      const params = new URLSearchParams(location.search || '')
      const hasMeaningfulQuery =
        params.has('member_id') ||
        params.has('start_time') ||
        params.has('end_time') ||
        params.has('has_member') ||
        params.has('has_watermark') ||
        params.has('departments') ||
        params.has('departmentUsers') ||
        params.has('filter_item_content') ||
        params.has('form_place')

      lastInitKeyRef.current = nextKey
      if (!isFirstInit && !hasMeaningfulQuery) {
        return
      }

      // 默认切换到按水印内容 Tab
      dispatch(setPhotoListActiveTab('content'))

      // 清空选中状态
      dispatch(clearCheck())

      // 检查 groupId
      const currentGroupId = groupId || localStorage.getItem('group_id')
      if (!currentGroupId) {
        navigate('/choose_group', { replace: true })
        return
      }

      // 初始化筛选条件
      const queryFilter = await handleInitFilter()

      // 获取照片
      if (queryFilter && !queryFilter.groupByUser) {
        const params = buildFetchPhotosParamsWithOptions({
          queryFilter,
          groupID: currentGroupId,
          isAscSort: false
        })

        try {
          const result = await (dispatch(fetchPhotosV2(params)) as any).unwrap()

          // 初始化猜你想搜及无结果推荐
          const hasResults = result?.total > 0
          await fetchSuggestedData(
            queryFilter,
            currentGroupId,
            hasResults,
            result
          )
        } catch (error) {
          capturePromiseException(error as Error)
        }
      }

      // 处理 form_place
      const { form_place } = parseQueryString()
      if (form_place) {
        if (form_place === 'data_statistics_member') {
          setFormPlace('dataStatisticStaffManage')
        } else if (form_place === 'data_statistics_watermark') {
          setFormPlace('dataStatisticWatermarkManage')
        }
      }

      // 获取 A/B 测试配置
      abtest.fastFetchABTest({
        param_name: 'VisualSearchAB',
        default_value: false,
        value_type: 'Boolean',
        callback: (res) => {
          if (res) {
            dispatch(setAIVisualSearchAB(res))
          }
        }
      })
    }

    init()

    return () => {
      dispatch(resetPhotosByPrecise())
    }
  }, [
    dispatch,
    groupId,
    handleExpandPrecise,
    location.pathname,
    location.search
  ])

  // 同步最新的 queryFilter
  useEffect(() => {
    if (currentQueryFilter) {
      latestFilterRef.current = currentQueryFilter
    }
  }, [currentQueryFilter])

  // 根据 expandInfo 触发精准搜索
  useEffect(() => {
    const filterForPrecise = latestFilterRef.current
    if (!filterForPrecise) return

    const normalizedExpandInfo = expandInfo ?? null
    if (normalizedExpandInfo === lastExpandInfoRef.current) {
      return
    }

    lastExpandInfoRef.current = normalizedExpandInfo
    handleExpandPrecise(filterForPrecise, normalizedExpandInfo)
  }, [expandInfo, handleExpandPrecise])

  const canPreciseSearch = preciseSearchState?.canPreciseSearch
  const isGroupByUser = Boolean(currentQueryFilter?.groupByUser)
  const shouldRenderPreciseTab =
    !isGroupByUser &&
    ((canPreciseSearch && precisePhotosNum > 0) || activeTab === 'precise')
  const shouldShowTabs =
    !isGroupByUser && (precisePhotosNum > 0 || activeTab === 'precise')

  const contentTabLabel = shouldRenderPreciseTab
    ? `按水印内容(${photosNum > 9999 ? '9999+' : photosNum})`
    : '按水印内容'

  const preciseTabLabel = (
    <Badge dot={showPreciseRedDot} offset={[6, 0]}>
      精准搜索({precisePhotosNum > 9999 ? '9999+' : precisePhotosNum})
    </Badge>
  )

  const downloadHistorySource = shouldShowTabs ? activeTab : 'content'

  function getAggReturnInfo() {
    const { from, snapshotKey } = parseQueryString() as any
    if (from !== 'agg_user' || !snapshotKey) return null
    const storageKey = `photoAggReturn:${snapshotKey}`
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      return { snapshotKey: String(snapshotKey), storageKey, parsed }
    } catch {
      return null
    }
  }

  function clearAggReturnParamsFromUrl() {
    const params = new URLSearchParams(window.location.search || '')
    const snapshotKey = params.get('snapshotKey')
    const from = params.get('from')
    if (from !== 'agg_user') return
    params.delete('from')
    params.delete('snapshotKey')
    // 从按人员聚合跳转到 member 详情后，一旦用户主动搜索/重置，就回到“全部照片”语义
    params.delete('member_id')
    navigate(
      window.location.pathname + (params.toString() ? `?${params}` : ''),
      { replace: true }
    )
    if (snapshotKey) {
      sessionStorage.removeItem(`photoAggReturn:${snapshotKey}`)
    }
  }

  const aggReturnInfo = getAggReturnInfo()
  const aggReturnAction = aggReturnInfo ? (
    <Button
      className="keywords-back-btn"
      onClick={() => {
        const snapshot = aggReturnInfo.parsed || {}
        const rawFilter = snapshot.searchFilter || snapshot.uiFilter || null
        if (!rawFilter) {
          clearAggReturnParamsFromUrl()
          return
        }

        const nextFilter: any = { ...rawFilter }
        // times 从 string[] 还原成 dayjs
        if (
          Array.isArray(nextFilter.times) &&
          nextFilter.times[0] &&
          nextFilter.times[1]
        ) {
          nextFilter.times = [
            dayjs(nextFilter.times[0]),
            dayjs(nextFilter.times[1])
          ]
        }

        dispatch(setPhotoFilterUi(nextFilter))
        dispatch(setPhotoFilterSearch(nextFilter))
        dispatch(setSearchTrackingId(String(Date.now())))
        dispatch(setPhotoListActiveTab('content'))

        // 去掉 member_id / from / snapshotKey，回到“全部照片”入口
        navigate('/photo/all', { replace: true })
        sessionStorage.removeItem(aggReturnInfo.storageKey)
      }}
    >
      ← 返回按人员结果
    </Button>
  ) : null

  // AI 入口:挂在搜索框「外面」,跟搜索按钮并排但是独立卡片
  // 不论侧栏开关都常驻,点击 toggle 当前侧栏状态
  const handleAiEntryClick = useCallback(() => {
    // 旧埋点改造 (rid=1093 web_workgroup_picspage_click): 新增 clickItem=点击AI问照片
    trackPicsPageClickV2({
      clickItem: '点击AI问照片',
      requirement_id: 1093
    })
    // 引导气泡和入口按钮共用同一个 wrap ref,点按钮被判定为"内部点击",
    // 不会触发外部点击监听里的 dismiss。这里显式关掉,避免气泡叠在面板上,
    // 同时把 localStorage 标记写上,下次进入不再弹。
    // trackClose=false:此处已独立上报"点击AI问照片",不再补"关闭弹窗"埋点污染漏斗。
    if (showAiGuide) dismissAiGuide(false)
    aiPanelFromPlaceRef.current = '点击AI问照片按钮'
    toggleAiPanel()
  }, [toggleAiPanel, showAiGuide, dismissAiGuide])

  const handleQuickAsk = useCallback((question: string) => {
    setHoverCardVisible(false)
    setPendingQuestion(question)
    aiPanelFromPlaceRef.current = '点击AI问照片按钮'
    setAiPanelVisible(true)
  }, [])

  const handleStartChat = useCallback(() => {
    setHoverCardVisible(false)
    handleAiEntryClick()
  }, [handleAiEntryClick])

  // 进入页面后预拉一次后端下发的快捷问题 (按团队个性化); ref 防重复请求。
  // 成功有数据 → 用后端的; 失败 / 空 → 卡片回退内置默认。无论结果都结束 loading。
  const loadPresetQuestions = useCallback(() => {
    if (presetQuestionsLoadedRef.current) return
    if (!userId || !groupId) return
    presetQuestionsLoadedRef.current = true
    const url = `${__API_SERVER_HOST__}/web/xiaohei-claw/preset_questions?user_id=${encodeURIComponent(
      String(userId)
    )}&group_id=${encodeURIComponent(String(groupId))}`
    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`preset_questions HTTP ${res.status}`)
        return res.json()
      })
      .then((data: PresetQuestionsResponse) => {
        const list = data?.preset_questions
        if (Array.isArray(list) && list.length > 0) setPresetQuestions(list)
        setPresetLoading(false)
      })
      .catch((err) => {
        // 拉取失败不阻断入口: 结束 loading, 卡片回退到内置兜底问题
        console.warn('[ai-photo-king] preset_questions 拉取失败:', err)
        setPresetLoading(false)
      })
  }, [userId, groupId])

  // 进入全部照片页 + 命中入口实验后立即预拉 (不等 hover): 这样 hover 时数据通常已就绪,
  // 不会先闪内置兜底再跳成后端动态问题 (danyang 反馈)。ref 防重复, userId/groupId 就绪才真发。
  useEffect(() => {
    if (!showAiPhotoKingEntry) return
    loadPresetQuestions()
  }, [showAiPhotoKingEntry, loadPresetQuestions])

  // showAiPhotoKingEntry=false 时整个入口卡 + 引导气泡都不渲染;
  // 传 undefined 给 keywordsRowAfter, PhotoFilter 那边按 prop falsy 跳过插槽。
  const aiPhotoEntry = !showAiPhotoKingEntry ? null : (
    <div
      className="ai-photo-king-entry-wrap"
      ref={aiGuideRef}
      onMouseEnter={() => setHoverCardVisible(true)}
      onMouseLeave={() => setHoverCardVisible(false)}
    >
      <button
        type="button"
        className="ai-photo-king-entry"
        onClick={handleAiEntryClick}
      >
        <span className="ai-photo-king-entry__icon" aria-hidden>
          <IconAiStar size={28} />
        </span>
        <span className="ai-photo-king-entry__label">AI 统计照片</span>
      </button>
      {/* presetLoading 时整张卡片不出: 数据没返回前不显示任何东西 (张璇 2026-06) */}
      {hoverCardVisible &&
        !showAiGuide &&
        !aiPanelVisible &&
        !presetLoading && (
          <div className="ai-quick-ask-card-popover">
            <AiQuickAskCard
              onStartChat={handleStartChat}
              onQuickAsk={handleQuickAsk}
              questions={presetQuestions}
              loading={presetLoading}
            />
          </div>
        )}
      {showAiGuide && (
        <div className="ai-entry-guide">
          <div className="ai-entry-guide__arrow" />
          <div className="ai-entry-guide__body">
            <div className="ai-entry-guide__header">
              <div className="ai-entry-guide__title-row">
                <GuideSparkle className="ai-entry-guide__sparkle" aria-hidden />
                <span className="ai-entry-guide__title">AI 统计照片</span>
              </div>
              <img
                className="ai-entry-guide__art"
                src={guideArtUrl}
                alt=""
                aria-hidden
              />
              <button
                type="button"
                className="ai-entry-guide__close"
                onClick={() => {
                  // × 关闭按钮 = 仅关引导气泡, 不应顺带打开 AI 面板。
                  // dismissAiGuide() 默认会上报 ai_b8113_click=关闭弹窗。
                  dismissAiGuide()
                }}
              >
                ✕
              </button>
            </div>
            <div className="ai-entry-guide__subtitle">
              10 秒生成报表 · 台账 · 趋势分析
              <br />
              代替手工 2 小时整理
            </div>
            <div className="ai-entry-guide__divider" />
            <div className="ai-entry-guide__examples">
              <div className="ai-entry-guide__ex-row">
                <GuideExPhoto className="ai-entry-guide__ex-icon" aria-hidden />
                <div className="ai-entry-guide__ex-text">
                  <div className="ai-entry-guide__ex-label">照片台账</div>
                  <div className="ai-entry-guide__ex-desc">
                    按拍摄人生成 5 月照片台账，每人一行
                  </div>
                </div>
              </div>
              <div className="ai-entry-guide__ex-row">
                <GuideExList className="ai-entry-guide__ex-icon" aria-hidden />
                <div className="ai-entry-guide__ex-text">
                  <div className="ai-entry-guide__ex-label">施工内容汇总</div>
                  <div className="ai-entry-guide__ex-desc">
                    按日期、施工区域汇总上月每天施工内容
                  </div>
                </div>
              </div>
              <div className="ai-entry-guide__ex-row">
                <GuideExChart className="ai-entry-guide__ex-icon" aria-hidden />
                <div className="ai-entry-guide__ex-text">
                  <div className="ai-entry-guide__ex-label">工时统计</div>
                  <div className="ai-entry-guide__ex-desc">
                    按工作日、周末、节假日统计团队 5 月工时
                  </div>
                </div>
              </div>
            </div>
            <div className="ai-entry-guide__benefit">
              <span aria-hidden>💎</span>
              <span>免费送 50 万积分体验</span>
              <img
                className="ai-entry-guide__gift"
                src={guideGiftUrl}
                alt=""
                aria-hidden
              />
            </div>
            <button
              type="button"
              className="ai-entry-guide__cta"
              onClick={() => {
                // 旧埋点改造: 引导气泡 CTA 也是 AI 入口, 上报 clickItem
                trackPicsPageClickV2({
                  clickItem: '点击AI问照片',
                  requirement_id: 1093
                })
                dismissAiGuide()
                aiPanelFromPlaceRef.current = 'AI问照片新功能弹窗'
                toggleAiPanel()
              }}
            >
              立即体验
              <GuideArrow className="ai-entry-guide__cta-arrow" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <Layout
      className="Photo ai-photo-king-page"
      style={{ flexDirection: 'row', minWidth: 0 }}
    >
      <Content
        className="content"
        style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // 撑满父级 (main.content / ant-layout-content),而不是按 viewport 估算
            // 一个固定值。之前用 minHeight: calc(100vh - 126px) 在大屏幕下算出的
            // 数字比 main.content 实际高度小,my-div 不撑满,main.content 底部
            // 露出一条白色留白(用户报"白条挡住照片底部")。height: 100% 直接对齐
            // ant-layout-content 的 stretch 高度,不再有缝。
            // 同时给内层 flex:1 + min-height:0 的子链(photo-list-pane → photoList →
            // photoList__virtual-body → AutoSizer)一个稳定的高度源,Grid 不会塌。
            height: '100%',
            background: '#fff',
            borderRadius: 12
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative' as const
            }}
          >
            <div className="photo-filter-with-chip">
              <PhotoFilter
                groupId={groupId || ''}
                formPlace={formPlace}
                fromPage="ai_photo_king"
                onSubmit={handleFilterSubmit}
                onReset={handleFilterReset}
                extraKeywordAction={aggReturnAction || undefined}
                keywordsRowAfter={aiPhotoEntry}
                hideQuickRow={
                  !!(
                    currentQueryFilter?.isNlpFilter &&
                    currentQueryFilter?.nlQuery
                  )
                }
              />
              {currentQueryFilter?.isNlpFilter &&
                currentQueryFilter?.nlQuery && (
                  <div className="ai-search-chip-row">
                    <div className="ai-search-chip">
                      <span className="ai-search-chip__icon" aria-hidden>
                        <IconSparkle size={13} />
                      </span>
                      <span className="ai-search-chip__label">AI 搜索</span>
                      <span
                        className="ai-search-chip__query"
                        title={currentQueryFilter.nlQuery}
                      >
                        {currentQueryFilter.nlQuery}
                      </span>
                      {typeof photosNum === 'number' && photosNum > 0 && (
                        <span className="ai-search-chip__count">
                          · {photosNum} 张
                        </span>
                      )}
                      <button
                        type="button"
                        className="ai-search-chip__clear"
                        aria-label="清除 AI 搜索"
                        onClick={clearAiSearchChip}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
            </div>
            {shouldShowTabs ? (
              <Tabs
                className="all-photo-v2-tabs"
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflow: 'hidden'
                }}
                activeKey={activeTab}
                onTabClick={(key) => {
                  trackPicsPageClickV2({
                    clickItem:
                      key === 'content'
                        ? 'byPhotoTab'
                        : key === 'precise'
                          ? 'byAccurateSearchTab'
                          : 'byPhotoTab'
                  })
                  if (key === 'precise') {
                    dispatch(setShowRedDot(false))
                  }
                }}
                onChange={(key) => {
                  dispatch(setPhotoListActiveTab(key as 'content' | 'precise'))
                }}
              >
                <TabPane
                  tab={contentTabLabel}
                  key="content"
                  className="photo-list-pane"
                >
                  <PhotosList
                    photoListRef={photoListRef}
                    searchType={currentSearchType}
                  />
                </TabPane>
                {shouldRenderPreciseTab && (
                  <TabPane
                    tab={preciseTabLabel}
                    key="precise"
                    className="photo-list-pane"
                  >
                    <PhotosListPrecise photoListRef={photoListRef} />
                  </TabPane>
                )}
              </Tabs>
            ) : isGroupByUser ? (
              <PhotosListByUserAgg photoListRef={photoListRef} />
            ) : (
              <div
                className="photo-list-pane all-photo-v2-list-pane--single"
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column' as const
                }}
              >
                <PhotosList
                  photoListRef={photoListRef}
                  searchType={currentSearchType}
                />
              </div>
            )}
          </div>
        </div>
        <DownloadHistory
          dataSource={downloadHistorySource}
          showDesktopClientPromo={true}
        />
        <ClientDownloadDrawer />
      </Content>
      {/*
        AI照片王 浮窗:始终挂载,靠 open prop 切 display:none,避免重开时丢状态或中断 SSE。
        浮窗折叠后用筛选行的 .ai-photo-king-entry 重新唤起,不再摆右下角 FAB。
        AB 实验门:showAiPhotoKingEntry=false 直接不挂浮窗组件, 实验组外不浪费 mount 成本。
      */}
      {showAiPhotoKingEntry && (
        <AiPhotoJunPanel
          userId={userId || ''}
          userName={userName || ''}
          groupId={groupId || ''}
          open={aiPanelVisible}
          openFromPlace={aiPanelFromPlaceRef.current}
          photoNumber={typeof photosNum === 'number' ? photosNum : 0}
          onMinimize={closeAiPanel}
          onApplyPageFilter={handleApplyPageFilter}
          onApplyNlQuery={handleApplyNlQuery}
          pendingQuestion={pendingQuestion}
          onPendingQuestionSent={() => setPendingQuestion('')}
        />
      )}
    </Layout>
  )
}

export default AllPhotoV2
